import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { businessInfo } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Services', href: '#services' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Reviews', href: '#testimonials' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#FAF6F2]/90 backdrop-blur-md border-b border-[#1A0B0E]/10 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between">
          {/* Logo — editorial serif */}
          <button
            data-testid="header-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-baseline gap-1 hover:opacity-70 transition-opacity"
          >
            <span className="font-serif-ed font-black text-2xl text-[#1A0B0E] tracking-tight">
              Stiletto
            </span>
            <span className="font-script text-2xl text-[#FF2D87] -ml-1">nails</span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.label}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => scrollToSection(item.href)}
                className="group relative text-[13px] tracking-[0.15em] uppercase font-medium text-[#1A0B0E] hover:text-[#FF2D87] transition-colors"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#FF2D87] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${businessInfo.phone}`}
              className="font-mono-ed text-xs tracking-wider text-[#1A0B0E]/70 hover:text-[#FF2D87] transition-colors"
            >
              786 · 423 · 3723
            </a>
            <button
              data-testid="header-book-btn"
              onClick={() => scrollToSection('#booking')}
              className="group inline-flex items-center gap-2 bg-[#1A0B0E] text-[#FAF6F2] pl-5 pr-1.5 py-1.5 rounded-full hover:bg-[#FF2D87] transition-colors duration-500"
            >
              <span className="text-xs tracking-wider uppercase">Book</span>
              <span className="bg-[#FF2D87] group-hover:bg-[#1A0B0E] rounded-full p-2 transition-colors duration-500">
                <ArrowUpRight className="w-3 h-3" />
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1A0B0E]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1A0B0E]" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-6 border-t border-[#1A0B0E]/10 animate-fade-in">
            <nav className="flex flex-col gap-5">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="text-left text-sm tracking-[0.15em] uppercase text-[#1A0B0E] hover:text-[#FF2D87] transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={`tel:${businessInfo.phone}`}
                className="font-mono-ed text-xs tracking-wider text-[#1A0B0E]/70"
              >
                786 · 423 · 3723
              </a>
              <button
                onClick={() => scrollToSection('#booking')}
                className="inline-flex w-fit items-center gap-2 bg-[#1A0B0E] text-[#FAF6F2] px-5 py-2.5 rounded-full"
              >
                <span className="text-xs tracking-wider uppercase">Book Appointment</span>
                <ArrowUpRight className="w-3 h-3" />
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
